import React, { useEffect } from 'react'

// Components
import TextInput from 'components/input/TextInput'
import DateInput from 'components/input/DateInput'
import PhoneNumberInput from 'components/special-input/PhoneNumberInput'

// Data
import {
    titles,
    genders,
    address_options
} from 'utils/constants/data'

function PersonalDetails({
    values,
    errors,
    touched,
    setFieldValue,
    setFieldTouched,
    handleChange,
    handleBlur,
}) {

    useEffect(() => {
        if (values.title !== "Other") {
            setFieldValue("other_title", "")
        }
        if (values.single_name_only) {
            setFieldValue("first_name", "")
            setFieldValue("second_name", "")
        }
        if (values.is_postal_same_as_residence) {
            setFieldValue("postal_building_name", "")
            setFieldValue("postal_unit_details", "")
            setFieldValue("postal_street_number", "")
            setFieldValue("postal_street_name", "")
            setFieldValue("postal_suburb", "")
            setFieldValue("postal_territory", "")
            setFieldValue("postal_postcode", "")
            setFieldValue("postal_delivery_info", "")
        }
    }, [
        values.title,
        values.single_name_only,
        values.is_postal_same_as_residence
    ]);

    return (
        <div className='bg-white rounded-lg mx-4 px-6 py-4 md:px-10 md:py-8 space-y-4 sm:space-y-6'>
            <p className='uppercase text-xl sm:text-2xl mb-2 md:mb-4 font-bold'>Personal Details</p>
            <hr className='h-1 border-none text-[#333] bg-[#333]' />
            <p className='uppercase text-lg sm:text-xl xl:text-2xl mb-2 md:mb-4 font-bold'>3. Enter your full name</p>
            <div className='flex flex-col space-y-2'>
                <p className='font-semibold flex text-base xl:text-lg'>Title:</p>
                <div className='flex flex-col space-y-2 md:flex-row md:items-center md:space-y-0 md:space-x-8'>
                    {titles.map((title, i) => {
                        return (
                            <div className='flex flex-row items-center space-x-3'>
                                <div>
                                    <input
                                        checked={values.title === title.title}
                                        id={`title-radio-btn-${i}`}
                                        name={`title-${i}`}
                                        type="radio"
                                        onChange={() => setFieldValue(`title`, title.title)}
                                        value={values.title}
                                        className="mt-1 w-4 h-4 md:w-6 md:h-6 text-blue-600 bg-white border-gray-300 peer-checked:bg-sky-600"
                                    />
                                </div>
                                <p className='text-base xl:text-lg' key={i}>{title.title}</p>
                            </div>
                        )
                    })}
                </div>
            </div>
            {values.title === "Other" && (
                <TextInput
                    name="other_title"
                    label='Other Title:'
                    placeholder="Fill in your other title..."
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={(errors?.other_title && touched?.other_title) && errors.other_title}
                    value={values.other_title}
                />
            )}
            {(errors?.title && touched?.title) && errors.title && (
                <p className='text-xs sm:text-sm xl:text-base text-red-500'>{errors.title}</p>
            )}
            <div className='flex flex-row items-center space-x-3'>
                <div>
                    <input
                        checked={values.single_name_only === true}
                        id={`single-name-checkbox-btn`}
                        name={`single-name`}
                        type="checkbox"
                        onChange={() => setFieldValue(`single_name_only`, !values.single_name_only)}
                        value={values.single_name_only}
                        className="mt-1 w-4 h-4 md:w-6 md:h-6 text-blue-600 bg-white border-gray-300 peer-checked:bg-sky-600"
                    />
                </div>
                <p className='text-base xl:text-lg' >Single-Name Only</p>
            </div>
            <p className='text-sm xl:text-base italic'>* Tick this box if you have one name only that cannot be written in the following format. Write your single name in the "Family Name" section.</p>
            <TextInput
                name="family_name"
                label='Family Name (Surname):'
                placeholder="Fill in your family name..."
                onChange={handleChange}
                onBlur={handleBlur}
                error={(errors?.family_name && touched?.family_name) && errors.family_name}
                value={values.family_name}
            />
            {values.single_name_only === false && (
                <div className='space-y-4 sm:space-y-6'>
                    <TextInput
                        name="first_name"
                        label='First Given Name:'
                        placeholder="Fill in your first name..."
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={(errors?.first_name && touched?.first_name) && errors.first_name}
                        value={values.first_name}
                    />
                    <TextInput
                        name="second_name"
                        label='Second Given Name (Middle):'
                        placeholder="Fill in your second or middle name..."
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={(errors?.second_name && touched?.second_name) && errors.second_name}
                        value={values.second_name}
                    />
                </div>
            )}
            <p className='text-sm xl:text-base italic'>* Please write the name that you used when you applied for your Unique Student Identifier (USI), including any middle names. If you do not yet have a USI and want [Name of RTO] to apply for a USI on your behalf, <b>you must write your name, including any middle names, exactly as written in the identity document</b> you choose to use for this purpose. See section on the USI at the end of this form for a detailed explanation.</p>
            <p className='uppercase text-lg sm:text-xl xl:text-2xl mb-2 md:mb-4 font-bold'>4. Enter your birth date</p>
            <DateInput
                name="birth_date"
                label='Month / Day / Year:'
                placeholder="Fill in your company or organization name..."
                onChange={handleChange}
                onBlur={handleBlur}
                error={(errors?.birth_date && touched?.birth_date) && errors.birth_date}
                value={values.birth_date}
            />
            <p className='uppercase text-lg sm:text-xl xl:text-2xl mb-2 md:mb-4 font-bold'>5. Gender</p>
            <div className='flex flex-col space-y-2 md:flex-row md:items-center md:space-y-0 md:space-x-8'>
                {genders.map((gender, i) => {
                    return (
                        <div className='flex flex-row items-center space-x-3'>
                            <div>
                                <input
                                    checked={values.gender === gender.title}
                                    id={`gender-radio-btn-${i}`}
                                    name={`gender-${i}`}
                                    type="radio"
                                    onChange={() => setFieldValue(`gender`, gender.title)}
                                    value={values.gender}
                                    className="mt-1 w-4 h-4 md:w-6 md:h-6 text-blue-600 bg-white border-gray-300 peer-checked:bg-sky-600"
                                />
                            </div>
                            <p className='text-base xl:text-lg' key={i}>{gender.title}</p>
                        </div>
                    )
                })}
            </div>
            {(errors?.gender && touched?.gender) && errors.gender && (
                <p className='text-xs sm:text-sm xl:text-base text-red-500'>{errors.gender}</p>
            )}
            <p className='uppercase text-lg sm:text-xl xl:text-2xl mb-2 md:mb-4 font-bold'>6. Enter your contact details</p>
            <PhoneNumberInput
                inputName="home_phone"
                selectName="home_phone_code"
                label="Home Phone:"
                placeholderInput="Fill in your home phone number..."
                onChangeInput={handleChange}
                onBlurInput={handleBlur}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                errorInput={(errors?.home_phone && touched?.home_phone) && errors.home_phone}
                errorSelect={(errors?.home_phone_code && touched?.home_phone_code) && errors.home_phone_code}
                inputValue={values.home_phone}
                selectValue={values.home_phone_code}
            />
            <PhoneNumberInput
                inputName="work_phone"
                selectName="work_phone_code"
                label="Work Phone:"
                placeholderInput="Fill in your work phone number..."
                onChangeInput={handleChange}
                onBlurInput={handleBlur}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                errorInput={(errors?.work_phone && touched?.work_phone) && errors.work_phone}
                errorSelect={(errors?.work_phone_code && touched?.work_phone_code) && errors.work_phone_code}
                inputValue={values.work_phone}
                selectValue={values.work_phone_code}
            />
            <PhoneNumberInput
                inputName="mobile_phone"
                selectName="mobile_phone_code"
                label="Mobile Phone:"
                placeholderInput="Fill in your mobile phone number..."
                onChangeInput={handleChange}
                onBlurInput={handleBlur}
                setFieldValue={setFieldValue}
                setFieldTouched={setFieldTouched}
                errorInput={(errors?.mobile_phone && touched?.mobile_phone) && errors.mobile_phone}
                errorSelect={(errors?.mobile_phone_code && touched?.mobile_phone_code) && errors.mobile_phone_code}
                inputValue={values.mobile_phone}
                selectValue={values.mobile_phone_code}
            />
            <TextInput
                name="email_address"
                label='Email Address:'
                placeholder="Fill in your email address..."
                onChange={handleChange}
                onBlur={handleBlur}
                error={(errors?.email_address && touched?.email_address) && errors.email_address}
                value={values.email_address}
                capitalize={false}
            />
            <TextInput
                name="alt_email_address"
                label='Alternative Email Address (Optional):'
                placeholder="Fill in your alternative email address..."
                onChange={handleChange}
                onBlur={handleBlur}
                error={(errors?.alt_email_address && touched?.alt_email_address) && errors.alt_email_address}
                value={values.alt_email_address}
                capitalize={false}
            />
            <p className='uppercase text-lg sm:text-xl xl:text-2xl mb-2 md:mb-4 font-bold'>7. What is the address of your usual residence?</p>
            <p className='text-base xl:text-lg text-justify mb-2 md:mb-4 '>Please provide the physical address (street number and name not post office box) where you usually reside rather than any
                temporary address at which you reside for training, work or other purposes before returning to your home.</p>
            <p className='text-base xl:text-lg text-justify mb-2 md:mb-4 '>If you are from a rural area use the address from your state or territory’s ‘rural property addressing’ or ‘numbering’ system as your residential street address.</p>
            <p className='text-base xl:text-lg text-justify mb-2 md:mb-4 '>Building/property name is the official place name or common usage name for an address site, including the name of a building,
                Aboriginal community, homestead, building complex, agricultural property, park or unbounded address site.</p>
            <TextInput
                name="building_name"
                label='Building / Property Name:'
                placeholder="Fill in your building or property name..."
                onChange={handleChange}
                onBlur={handleBlur}
                error={(errors?.building_name && touched?.building_name) && errors.building_name}
                value={values.building_name}
            />
            <TextInput
                name="unit_details"
                label='Flat / Unit Details:'
                placeholder="Fill in your flat or unit details..."
                onChange={handleChange}
                onBlur={handleBlur}
                error={(errors?.unit_details && touched?.unit_details) && errors.unit_details}
                value={values.unit_details}
            />
            <TextInput
                name="street_number"
                label='Street / Lot Number (e.g. 205 or Lot 118):'
                placeholder="Fill in your street or lot number..."
                onChange={handleChange}
                onBlur={handleBlur}
                error={(errors?.street_number && touched?.street_number) && errors.street_number}
                value={values.street_number}
            />
            <TextInput
                name="street_name"
                label='Street Name:'
                placeholder="Fill in your street name..."
                onChange={handleChange}
                onBlur={handleBlur}
                error={(errors?.street_name && touched?.street_name) && errors.street_name}
                value={values.street_name}
            />
            <TextInput
                name="suburb"
                label='Suburb, Locality or Town:'
                placeholder="Fill in your suburb, locality or town..."
                onChange={handleChange}
                onBlur={handleBlur}
                error={(errors?.suburb && touched?.suburb) && errors.suburb}
                value={values.suburb}
            />
            <TextInput
                name="territory"
                label='State / Territory:'
                placeholder="Fill in your state or territory..."
                onChange={handleChange}
                onBlur={handleBlur}
                error={(errors?.territory && touched?.territory) && errors.territory}
                value={values.territory}
            />
            <TextInput
                name="postcode"
                label='Postcode:'
                placeholder="Fill in your postcode..."
                onChange={handleChange}
                onBlur={handleBlur}
                error={(errors?.postcode && touched?.postcode) && errors.postcode}
                value={values.postcode}
            />
            <p className='uppercase text-lg sm:text-xl xl:text-2xl mb-2 md:mb-4 font-bold'>8. What is your postal address (If different from above)?</p>
            <div className='flex flex-col space-y-2'>
                {address_options.map((option, i) => {
                    return (
                        <div className='flex flex-row items-center space-x-3'>
                            <div>
                                <input
                                    checked={values.is_postal_same_as_residence === option.value}
                                    id={`postal-option-radio-btn-${i}`}
                                    name={`postal-option-${i}`}
                                    type="radio"
                                    onChange={() => setFieldValue(`is_postal_same_as_residence`, option.value)}
                                    value={values.is_postal_same_as_residence}
                                    className="mt-1 w-4 h-4 md:w-6 md:h-6 text-blue-600 bg-white border-gray-300 peer-checked:bg-sky-600"
                                />
                            </div>
                            <p className='text-base xl:text-lg' key={i}>{option.title}</p>
                        </div>
                    )
                })}
            </div>
            {(errors?.is_postal_same_as_residence && touched?.is_postal_same_as_residence) && errors.is_postal_same_as_residence && (
                <p className='text-xs sm:text-sm xl:text-base text-red-500'>{errors.is_postal_same_as_residence}</p>
            )}
            {values.is_postal_same_as_residence === false && (
                <div className='space-y-4 sm:space-y-6'>
                    <TextInput
                        name="postal_building_name"
                        label='Building / Property Name:'
                        placeholder="Fill in your building or property name..."
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={(errors?.postal_building_name && touched?.postal_building_name) && errors.postal_building_name}
                        value={values.postal_building_name}
                    />
                    <TextInput
                        name="postal_unit_details"
                        label='Flat / Unit Details:'
                        placeholder="Fill in your flat or unit details..."
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={(errors?.postal_unit_details && touched?.postal_unit_details) && errors.postal_unit_details}
                        value={values.postal_unit_details}
                    />
                    <TextInput
                        name="postal_street_number"
                        label='Street / Lot Number (e.g. 205 or Lot 118):'
                        placeholder="Fill in your street or lot number..."
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={(errors?.postal_street_number && touched?.postal_street_number) && errors.postal_street_number}
                        value={values.postal_street_number}
                    />
                    <TextInput
                        name="postal_street_name"
                        label='Street Name:'
                        placeholder="Fill in your street name..."
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={(errors?.postal_street_name && touched?.postal_street_name) && errors.postal_street_name}
                        value={values.postal_street_name}
                    />
                    <TextInput
                        name="postal_suburb"
                        label='Suburb, Locality or Town:'
                        placeholder="Fill in your suburb, locality or town..."
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={(errors?.postal_suburb && touched?.postal_suburb) && errors.postal_suburb}
                        value={values.postal_suburb}
                    />
                    <TextInput
                        name="postal_territory"
                        label='State / Territory:'
                        placeholder="Fill in your state or territory..."
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={(errors?.postal_territory && touched?.postal_territory) && errors.postal_territory}
                        value={values.postal_territory}
                    />
                    <TextInput
                        name="postal_postcode"
                        label='Postcode:'
                        placeholder="Fill in your postcode..."
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={(errors?.postal_postcode && touched?.postal_postcode) && errors.postal_postcode}
                        value={values.postal_postcode}
                    />
                    <TextInput
                        name="postal_delivery_info"
                        label='Postal Delivery Information (e.g. PO Box 254):'
                        placeholder="Fill in your postal delivery information..."
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={(errors?.postal_delivery_info && touched?.postal_delivery_info) && errors.postal_delivery_info}
                        value={values.postal_delivery_info}
                    />
                </div>
            )}
        </div>
    )
}

export default PersonalDetails